import React, { useState } from "react";
import { createShapeId } from "@tldraw/editor";
import { useIsBuilderEmptyState } from "pages/flyer/builder/hooks/useIsBuilderEmptyState";

interface IconProps {
  icon: {
    id: string;
    name?: string;
    thumbnail_url: string;
    svg: string; // Now guaranteed to be present
  };
  editor: any;
  options?: any; // Make optional since we won't need it for API calls anymore
  onIconSelected?: () => void;
  saveToBrandKit?: (svgContent: string, iconName: string) => void;
}

const Icon = React.memo(({ icon, editor, options, onIconSelected, saveToBrandKit }: IconProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const isEmptyState = useIsBuilderEmptyState();

  const handleMouseDown = (e: React.MouseEvent, id: string) => {
    if (isEmptyState) return;
    e.preventDefault();

    const initialX = e.clientX;
    const initialY = e.clientY;

    const handleMouseMove = () => {
      editor.setCursor({ type: "move" });
    };

    const handleMouseUp = (upEvent: MouseEvent) => {
      const isDrag = Math.abs(upEvent.clientX - initialX) > 5 || Math.abs(upEvent.clientY - initialY) > 5;
      setIsDragging(true);

      if (isDrag) {
        handleClickedICon(id, upEvent);
      } else {
        handleClickedICon(id);
      }

      setIsDragging(false);
      editor.setCursor({ type: "default" });
      window.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleClickedICon = async (id: string, upEvent?: MouseEvent) => {
    // When triggered from Brand Kit, only save — don't place on canvas
    if (saveToBrandKit) {
      const name = icon.name || `icon-${icon.id}`;
      saveToBrandKit(icon.svg, name);
      if (onIconSelected) {
        onIconSelected();
      }
      return;
    }

    setIsLoading(true);
    try {
      // SVG data is now guaranteed to be available for all returned icons

      // get center of the screen and convert it to page coordinates
      const { width, height } = editor.getViewportScreenBounds();
      const center = editor.screenToPage({ x: width / 2, y: height / 2 });

      // Create a unique shape ID first
      const shapeId = createShapeId();

      editor.createShape({
        id: shapeId,
        type: "customSvg",
        x: upEvent ? editor.screenToPage({ x: upEvent.clientX, y: upEvent.clientY }).x - 125 / 2 : center.x - 125 / 2,
        y: upEvent ? editor.screenToPage({ x: upEvent.clientX, y: upEvent.clientY }).y - 125 / 2 : center.y - 125 / 2,
        props: {
          w: 125,
          h: 125,
          svgPath: icon.svg,
          fill: "#000000",
          colorMix: { "0": { color: "#000000", isHidden: false } },
        },
      });

      // Switch to select tool and select the newly created icon
      editor.setCurrentTool("select");
      editor.select(shapeId);

      // Call callback to reset search if provided
      if (onIconSelected) {
        onIconSelected();
      }

      // Remove any custom cursor from drawing tools
      const canvas = document.querySelector(".tl-canvas") as HTMLElement;
      if (canvas) {
        canvas.style.removeProperty("cursor");
      }
    } catch (error) {
      console.error("Error adding icon to canvas:", error);

      // Show user-friendly error message
      const errorMessage = error instanceof Error ? error.message : "Failed to add icon to canvas";
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isDragging && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 text-white text-lg z-50 pointer-events-none">
          Drag and Drop icon
        </div>
      )}
      <div
        className={`w-full h-full rounded-[8px] object-cover cursor-pointer transition-all relative flex items-center justify-center ${
          isLoading ? "opacity-50" : ""
        }`}
        onMouseDown={(e) => !isLoading && handleMouseDown(e, icon.id)}
      >
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-[8px]">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          </div>
        )}
        {imageError ? (
          <div className="flex items-center justify-center w-full h-full bg-gray-200 rounded-[8px] text-gray-500 text-xs p-2">
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        ) : (
          <img
            src={icon.thumbnail_url}
            alt={icon.name || "icon"}
            className="w-full h-full object-contain rounded-[8px] p-2"
            onError={() => setImageError(true)}
            onLoad={() => setImageError(false)}
          />
        )}
      </div>
    </>
  );
});

export default Icon;
